import { useState } from 'react';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { Button, Card, Input, useToast } from '@nako/ui';
import { useAuthStore } from '@/stores/auth';
import { supabase } from '@/lib/supabase';

/**
 * Non-dismissable overlay shown when the signed-in user has been issued a
 * temporary password by an admin. Forces them to set a new one before they
 * can use the app. Once the new password is saved, profiles.must_change_password
 * is flipped back to false and the overlay disappears.
 */
export function ForcePasswordReset() {
  const profile = useAuthStore((s) => s.profile);
  const setProfile = useAuthStore.setState;
  const toast = useToast();

  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!profile?.must_change_password) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pwd.length < 8) {
      toast.show('Password must be at least 8 characters.', { tone: 'warning' });
      return;
    }
    if (pwd !== pwd2) {
      toast.show('Passwords do not match.', { tone: 'warning' });
      return;
    }
    if (pwd === 'admin123') {
      toast.show('Please pick a real password — not the temporary one.', { tone: 'warning' });
      return;
    }
    setBusy(true);
    const { error: pwErr } = await supabase.auth.updateUser({ password: pwd });
    if (pwErr) {
      setBusy(false);
      toast.show(pwErr.message, { tone: 'error' });
      return;
    }
    const { error: flagErr } = await supabase
      .from('profiles')
      .update({ must_change_password: false })
      .eq('id', profile.id);
    setBusy(false);
    if (flagErr) {
      toast.show(`Password saved, but flag failed: ${flagErr.message}`, { tone: 'warning' });
      return;
    }
    setProfile((s) => ({
      ...s,
      profile: s.profile ? { ...s.profile, must_change_password: false } : s.profile,
    }));
    toast.show('Password updated. Welcome to Nako Pula!', { tone: 'success' });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/85 backdrop-blur-sm">
      <Card className="m-4 w-full max-w-md">
        <div className="mb-4 flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <ShieldCheck size={24} />
          </span>
          <div>
            <h2 className="font-display text-xl">Set your password</h2>
            <p className="text-muted text-xs">One-time step before you start</p>
          </div>
        </div>

        <p className="text-muted mb-4 text-sm">
          You signed in with a temporary password. For your safety, please pick your own password
          before continuing. At least 8 characters.
        </p>

        <form onSubmit={submit} className="space-y-3">
          <Input
            type={show ? 'text' : 'password'}
            label="New password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            autoComplete="new-password"
            required
            autoFocus
          />
          <Input
            type={show ? 'text' : 'password'}
            label="Confirm new password"
            value={pwd2}
            onChange={(e) => setPwd2(e.target.value)}
            autoComplete="new-password"
            required
          />
          <label className="text-muted flex items-center gap-2 text-xs">
            <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
            Show passwords
          </label>
          <Button
            type="submit"
            full
            loading={busy}
            disabled={!pwd || !pwd2}
            leftIcon={<KeyRound size={16} />}
          >
            Save and continue
          </Button>
        </form>

        <p className="text-muted mt-4 text-center text-[11px]">
          Tsamaya le Pula — walk with the rain.
        </p>
      </Card>
    </div>
  );
}
